import callApi from '@niarab2c/frontend-commons/src/util/callApi';
import fetchHotelDetails from '@niarab2c/frontend-commons/src/util/fetchHotelDetails';
import { NiaraIcon } from '@niaratech/niara-icons';
import { useAsync } from '@niaratech/niara-react-components/src';
import _pick from 'lodash/pick';
import matchSorter from 'match-sorter';
import React, { useCallback, useMemo } from 'react';
import { useSelector } from '../app/base';
const FIELDS_TO_PICK = ['id', 'address', 'city', 'state', 'country', 'name', 'type', 'isFeatured'] as const;
type FieldsKey = (typeof FIELDS_TO_PICK)[number];
const ICONS = {
  city: <NiaraIcon icon="sysGlobeOutlined" size={26} />,
  hotel: <NiaraIcon icon="sysHotelOutlined" size={26} />,
  country: <NiaraIcon icon="htlPinOutlined" size={26} />,
  neighborhood: <NiaraIcon icon="sysNeighborhood" size={26} />,
  state: <NiaraIcon icon="htlPinOutlined" size={26} />,
  multi_city_vicinity: <NiaraIcon icon="sysMultiCity" size={26} />,
  point_of_interest: <NiaraIcon icon="sysFavorite" size={26} />,
  airport: <NiaraIcon icon="sysPlane" size={26} />,
  high_level_region: <NiaraIcon icon="htlPinOutlined" size={26} />,
  province_state: <NiaraIcon icon="htlPinOutlined" size={26} />,
  continent: <NiaraIcon icon="htlPinOutlined" size={26} />,
  train_station: <NiaraIcon icon="sysTrain" size={26} />,
  metro_station: <NiaraIcon icon="sysTrain" size={26} />
} as const;
type ContentSearchResponseResult = {
  id: string;
  name: string;
  contentType: 'property' | 'region';
  omniId?: string;
  type: 'city' | 'hotel' | 'all';
  regionIds?: string[];
  isFeatured?: boolean;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
};
type DestinationSearchItem = {
  id: string;
  label: string;
  value: {
    hotelIds?: string[];
    propertyId: string;
    contentType: 'property' | 'region';
    regionIds?: [];
  };
  isFeatured?: boolean;
  icon?: JSX.Element;
} & Pick<ContentSearchResponseResult, FieldsKey>;
const destinationSearchV2 = (clientId, tenantId, landingPageId, groupHotelsByCity: boolean) => async (query): Promise<DestinationSearchItem[]> => {
  const size = groupHotelsByCity ? 50 : undefined;
  let response = [];
  response = await callApi('niara-spear-content-integrations', '/content/search', 'get', {
    params: {
      q: query,
      clientId: clientId ?? undefined,
      tenantId: tenantId ?? undefined,
      landingPageId: landingPageId ?? undefined,
      size: size ?? undefined
    }
  });
  let results: Array<ContentSearchResponseResult> = response['map'] ? response : response['results']; // Quando é map e quando é results???

  // agrupar hotéis por cidade
  if (groupHotelsByCity) {
    // #231606 Não mostrar o hotel duplicado em mais de uma cidade
    // Trata casos em que existe cidade "duplicada", como uma englobando a outra 
    const _groupedHotels = [];
    const resultsGroupedByCity = results.filter(r => r.type == 'city').map(city => {
      const hotels = results.filter(r => r.type == 'hotel' && r?.regionIds?.includes(city?.id) && !_groupedHotels.includes(r));
      if (hotels.length > 0) {
        _groupedHotels.push(...hotels);
        return [{
          ...city,
          isFeatured: true
        }, ...results.filter(r => r.type == 'hotel' && r?.regionIds?.includes(city?.id))];
      } else {
        // filtra cidades "duplicadas"            
        return [];
      }
    }).flat(1);

    // hotéis que não apareceram em nenhuma cidade
    const ungroupedResults = results.filter(r => !resultsGroupedByCity.find(r2 => r2.id == r.id) && r.type == 'hotel');
    results = results.filter(r => r.type == 'all').map<ContentSearchResponseResult>(r => ({
      ...r,
      isFeatured: true
    })).concat(ungroupedResults).concat(resultsGroupedByCity);
  }
  return results?.map(r => {
    const hotelIds = r.type === 'hotel' && r.omniId ? [`HOTEL_OMNI_${r.omniId}`] : undefined;
    return {
      ..._pick(r, FIELDS_TO_PICK),
      value: {
        hotelIds,
        propertyId: r.id,
        contentType: r.contentType
      },
      regionIds: r?.regionIds,
      label: r.name,
      icon: groupHotelsByCity ? ICONS?.[r.type] : undefined
    };
  });
};
type UseDestinationSearchProps = {
  clientId?: string;
  groupHotelsByCity?: boolean;
};
const useDestinationSearch = ({
  clientId: clientIdCriteria,
  groupHotelsByCity
}: UseDestinationSearchProps): ReturnType<typeof destinationSearchV2> => {
  const {
    tenantId,
    clientId,
    landingPageId,
    user
  } = useSelector(root => {
    return {
      tenantId: root.core?.tenantId,
      clientId: root.core?.clientId || clientIdCriteria,
      landingPageId: root.storefrontConfig?.storefront?.landingPageId,
      user: root?.authentication?.user
    };
  });
  const [allowedHotels] = useAsync(() => user?.allowedHotels?.length > 0 && Promise.all(user?.allowedHotels?.map(async hotel => {
    const omniId = `HOTEL_OMNI_${hotel?.omniId}`;
    return fetchHotelDetails(omniId, undefined, tenantId, undefined).then(r => ({
      id: hotel?.id,
      address: [r?.address?.street, r?.address?.cityName, r?.address?.state, r?.address?.country].filter(Boolean).join(', '),
      city: r?.address?.cityName,
      state: r?.address?.state,
      country: r?.address?.country,
      name: r?.name,
      type: 'hotel',
      label: r.name,
      value: {
        hotelIds: [omniId],
        propertyId: hotel?.id,
        contentType: 'property'
      },
      regionIds: r?.regionIds
    }) as DestinationSearchItem);
  })).then(response => response), [tenantId, user?.allowedHotels]);
  const allowedDestinationSearch = useCallback(async (query?: string): Promise<DestinationSearchItem[]> => {
    const results = matchSorter(allowedHotels ?? [], query, {
      keys: ['name']
    });
    return results;
  }, [allowedHotels]);
  return useMemo(() => {
    if (user?.allowedHotels?.length > 0) {
      return allowedDestinationSearch;
    }
    return destinationSearchV2(clientId || clientIdCriteria, tenantId, landingPageId, groupHotelsByCity);
  }, [allowedDestinationSearch, clientId, clientIdCriteria, landingPageId, tenantId, user?.allowedHotels?.length, groupHotelsByCity]);
};
export default useDestinationSearch;